import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Flex, Link } from "rebass/styled-components";

import { NavLink } from "../Button";
import { Title1 } from "../../../components/text";
import { Button } from "../Button";
import { StudioMainToggle } from "../Button";
import theme from "../../../theme/themeThree";
import { Avatar } from "../Avatar";
import { NavListDropdown } from "./Components/Dropdown";
import { Tooltip } from "../Tooltip";
import {
  AvatarBorder,
  getAvatarImage,
  getAvatarBorderLevel,
} from "../../Utilities/User";
import { ReactComponent as StudioLogo } from "../../../assets/images/badges/studio_icon.svg";
import { EnabledCards } from "./Components/EnabledCards";
import { useIsDesktopApp } from "../../Hooks/useIsDesktopApp";
import { ReactComponent as CompLogo } from "../../Assets/Brand/AS_Comp_HORZ.svg";
import { ReactComponent as BrandLogo } from "../../Assets/Brand/A-Mark-Light.svg";
import { IAllstarUser } from "../../Views/Setup/Components/@types";

interface IUnAuthenticatedNavActionProps {
  isStudio?: boolean;
  isMobileApp?: boolean;
}

interface IAuthenticatedNavActionProps {
  allstarUser: IAllstarUser;
  logout: (logoutReason: string) => void;
  hideAvatar?: boolean;
  download?: () => void;
}

interface IOpenFacetProps {
  open: boolean;
  setOpen: (arg0: boolean) => void;
}

function UnAuthenticatedNavAction({
  isStudio,
  isMobileApp,
}: IUnAuthenticatedNavActionProps) {
  if (isMobileApp) return <></>;
  if (process.env.REACT_APP_GET_APP_BEFORE_SIGNUP) {
    return (
      <Button
        size="small"
        to={`/login?next=${window.location.pathname}`}
        flex={isStudio}
        ml={4}
      >
        Log in
      </Button>
    );
  }
  return (
    <>
      <StyledNavLink
        mr={theme.margins.navLinks}
        to={`/login?next=${window.location.pathname}`}
        flexShrink="0"
      >
        Login
      </StyledNavLink>
      <Button size="small" to="/signup">
        Sign up now
      </Button>
    </>
  );
}

function AuthenticatedNavAction({
  allstarUser,
  logout,
  hideAvatar,
}: IAuthenticatedNavActionProps) {
  if (hideAvatar) return <></>;

  const avatarBorderLevel = getAvatarBorderLevel(allstarUser?.user);

  return (
    <DropdownContainerAvatar
      position={{
        top: "calc(100% - 6px)",
        right: "-5px",
      }}
      tooltipContent={
        <NavListDropdown allstarUser={allstarUser} logout={logout} />
      }
    >
      {!allstarUser?.user.hasUsername ? (
        <AvatarNotification size="14px" />
      ) : null}
      <AvatarContainer>
        <AvatarBorder type={avatarBorderLevel} size={"nav"}>
          <Avatar size={"nav"} image={getAvatarImage(allstarUser?.user)} />
        </AvatarBorder>
      </AvatarContainer>
      <AvatarContainerMobile>
        <AvatarBorder type={avatarBorderLevel} size={"tiny"}>
          <Avatar size={"tiny"} image={getAvatarImage(allstarUser?.user)} />
        </AvatarBorder>
      </AvatarContainerMobile>
    </DropdownContainerAvatar>
  );
}

export function StudioAuthNavActions({
  allstarUser,
  logout,
  download,
  enabledCardsVisible,
  setEnabledCardsVisible,
  view,
  setView,
  isVisible,
  toggleVisible,
  isMobileApp,
}: any) {
  const { t } = useTranslation("translation", {
    keyPrefix: "studio",
  });
  const { isDesktop } = useIsDesktopApp();

  return (
    <ResponsiveStudioNavActions
      alignItems="center"
      flexDirection="row"
      justifyContent="space-between"
      sx={theme.studioNavBase}
      variant="studioNav"
    >
      <Flex justifyContent="flex-start" alignItems="center" flexDirection="row">
        <OpenFacet open={isVisible} setOpen={toggleVisible} />
        <StyledLogo>
          {!isDesktop && !isMobileApp && (
            <>
              <Link href="/clips">
                <StudioBrandLogo />
              </Link>
              <Spacer />
            </>
          )}
          <StudioLogo style={{ height: "40px", width: "40px" }} />
          <StyledStudioText>{t("logo")}</StyledStudioText>
        </StyledLogo>
      </Flex>
      <StudioMainToggle view={view} setView={setView} />
      <Flex alignItems="center" justifyContent="space-between">
        <EnabledCards
          enabledCardsVisible={enabledCardsVisible}
          setEnabledCardsVisible={setEnabledCardsVisible}
        />
        {isDesktop || isMobileApp ? (
          <></>
        ) : allstarUser.loggedIn ? (
          <AuthenticatedNavAction
            allstarUser={allstarUser}
            download={download}
            logout={logout}
            hideAvatar={isMobileApp}
          />
        ) : (
          <UnAuthenticatedNavAction isStudio={true} />
        )}
      </Flex>
    </ResponsiveStudioNavActions>
  );
}

const StyledLogo = styled(Flex)`
  flex-direction: row;
  align-items: center;
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    display: none;
    margin-left: 0px;
  }
`;

const StyledFixedButton = styled(Flex)`
  display: none;
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    display: flex;
    padding: 5px;
    z-index: 9;
    border-radius: 999px;
  }
`;

const OpenFacet = ({ open, setOpen }: IOpenFacetProps) => {
  return (
    <StyledFixedButton
      open={open}
      onClick={() => setOpen(!open)}
      justifyContent="center"
      alignItems="center"
    >
      <Burger open={open} setOpen={setOpen} />
    </StyledFixedButton>
  );
};

const StyledBurger = styled.button<{ open: boolean }>`
  align-self: center;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 20px;
  height: 21px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 9;
  display: none;
  &:focus {
    outline: none;
  }
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    display: flex;
  }
  div {
    width: 24px;
    height: 2px;
    background: ${({ open }) => (open ? "#4DBD94" : "#8FA9AF")};
    border-radius: 999px;
    transition: all 0.2s linear;
    position: relative;
    transform-origin: 2px;
    :first-child {
      transform: ${({ open }) => (open ? "rotate(45deg)" : "rotate(0)")};
    }
    :nth-child(2) {
      opacity: ${({ open }) => (open ? "0" : "1")};
      transform: ${({ open }) => (open ? "translateX(0px)" : "translateX(0)")};
    }
    :nth-child(3) {
      transform: ${({ open }) => (open ? "rotate(-45deg)" : "rotate(0)")};
    }
  }
`;

const Burger = ({ open, setOpen }: IOpenFacetProps) => {
  return (
    <StyledBurger open={open} onClick={() => setOpen(!open)}>
      <div />
      <div />
      <div />
    </StyledBurger>
  );
};

const StyledNavLink = styled(NavLink)`
  margin-right: ${(props) => props.theme.margins.navLinks};
`;

const ResponsiveStudioNavActions = styled(Flex)`
  @media (max-width: ${(props) => props.theme.breaks.medium}) {

`;

const DropdownContainerAvatar = styled(Tooltip)`
  &:hover {
    cursor: default;
  }
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    padding-right: 0;
  }
`;

const AvatarNotification = styled(Flex)`
  position: absolute;
  right: 0px;
  border-radius: 999px;
  background: ${(props) => props.theme.colors.redGradient};
  border: 1px solid ${(props) => props.theme.colors.ice};
`;

const AvatarContainer = styled(Flex)`
  width: 46px;
  height: 46px;
  background: ${(props) => props.theme.colors.ned};
  padding: 2px;
  border-radius: 999px;
  align-items: center;
  &:hover {
    cursor: pointer;
  }
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    display: none;
  }
`;

const AvatarContainerMobile = styled(Flex)`
  display: none;
  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    display: flex;
    width: 34px;
    height: 30px;
    background: transparent;
    align-items: center;
    justify-content: center;
  }
`;

const StyledStudioText = styled(Title1)`
  font-weight: 500;
  margin-left: 5px;
  display: flex;
`;

const StudioBrandLogo = styled(BrandLogo)`
  height: 38px;
  width: 38px;
  margin-top: 2px;
`;

const Spacer = styled.div`
  height: 58px;
  padding-left: 16px;
  margin-right: 16px;
  border-right: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
`;
